import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  List,
  Divider,
  Tooltip,
  useTheme,
  ListItem,
  Typography,
  IconButton,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  CircularProgress,
  Collapse,
  TextField,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ReportDetails,
  staticReportGroups,
} from '../config/reportGroups';
import { useReportGroups } from '../context/ReportGroupsContext';

// Vendor-specific fullscreen types
type VendorFullscreenDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
  mozCancelFullScreen?: () => Promise<void> | void;
};

type VendorFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
};

const CategoryReportsPage: React.FC = () => {
  const theme = useTheme();
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();

  const { reportGroups, loading, error } = useReportGroups();

  const [iFrameUrl, setIFrameUrl] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeReport, setActiveReport] = useState<ReportDetails | null>(null);
  const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set());
  const [paramValues, setParamValues] = useState<{ [key: string]: string }>({});

  // Current category data
  const currentCategory = useMemo(
    () =>
      reportGroups.find((group) => group.id === categoryId) ||
      staticReportGroups.find((group) => group.id === categoryId),
    [reportGroups, categoryId],
  );

  // Fetch report groups from API or use static config
  useEffect(() => {
    if (!currentCategory) {
      setActiveReport(null);
      setIFrameUrl('');
      return;
    }

    // Check for flat reports first
    const reportKeys = Object.keys(currentCategory.reports ?? {});
    if (reportKeys.length > 0) {
      setActiveReport(currentCategory.reports![reportKeys[0]]);
      return;
    }

    // Check for nested subcategories
    const subcategoryKeys = Object.keys(currentCategory.subcategories ?? {});
    if (subcategoryKeys.length > 0) {
      const firstSubcategory = currentCategory.subcategories![subcategoryKeys[0]];
      const firstSubcategoryReportKeys = Object.keys(firstSubcategory.reports);
      if (firstSubcategoryReportKeys.length > 0) {
        setActiveReport(firstSubcategory.reports[firstSubcategoryReportKeys[0]]);
        // Auto-expand the first subcategory
        setExpandedSubcategories(new Set([subcategoryKeys[0]]));
        return;
      }
    }

    setActiveReport(null);
    setIFrameUrl('');
  }, [currentCategory, categoryId]);

  // Initialize parameter values when active report changes
  useEffect(() => {
    if (activeReport?.parameters) {
      const defaultValues: { [key: string]: string } = {};
      activeReport.parameters.forEach((param) => {
        if (param.defaultValue !== undefined && param.defaultValue !== null) {
          if (typeof param.defaultValue === 'string') {
            defaultValues[param.slug] = param.defaultValue;
          }
        }
      });
      setParamValues(defaultValues);
    } else {
      setParamValues({});
    }
  }, [activeReport]);

  // Generate iframe URL when active report changes
  useEffect(() => {
    const generateEmbedUrl = async () => {
      if (activeReport) {
        if (activeReport.embed_url) {
          // Use public embed URL with parameters
          let url = activeReport.embed_url;
          
          // Append parameters if any
          if (Object.keys(paramValues).length > 0) {
            const queryParams = new URLSearchParams();
            Object.entries(paramValues).forEach(([key, value]) => {
              if (value) queryParams.append(key, value);
            });
            const separator = url.includes('?') ? '&' : '?';
            url += `${separator}${queryParams.toString()}`;
          }
          
          setIFrameUrl(url);
        } else if (activeReport.dashboard_id) {
          // Use backend API for secure signed embedding
          try {
            const response = await fetch('/api/metabase/embed', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                dashboard_id: activeReport.dashboard_id,
                params: paramValues,
              }),
            });

            if (!response.ok) {
              const errorText = await response.text();
              console.error('Embed URL error:', errorText);
              throw new Error(`Failed to get embed URL: ${response.statusText}`);
            }

            const data = await response.json();
            setIFrameUrl(data.embed_url);
          } catch (error) {
            console.error('Failed to generate embed URL:', error);
          }
        }
      }
    };

    generateEmbedUrl();
  }, [activeReport, paramValues]);

  // Fullscreen event listeners
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  const toggleFullscreen = () => {
    const el = iframeContainerRef.current as VendorFullscreenElement | null;
    if (!el) return;

    const vdoc = document as unknown as VendorFullscreenDocument;

    if (!document.fullscreenElement) {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else if (el.msRequestFullscreen) el.msRequestFullscreen();
      else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (vdoc.webkitExitFullscreen) vdoc.webkitExitFullscreen();
      else if (vdoc.msExitFullscreen) vdoc.msExitFullscreen();
      else if (vdoc.mozCancelFullScreen) vdoc.mozCancelFullScreen();
    }
  };

  return (
    <Box pb={3}>
      <Box
        flexDirection="column"
        justifyContent="flex-start"
        bgcolor={theme.palette.background.paper}
        sx={{
          borderRadius: '14px',
          boxShadow: '0px 2px 16px 0px #1E315F1A',
        }}
      >
        {/* Header */}
        <Box
          py="10px"
          px="20px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="subtitle1"
              style={{ fontWeight: '500', lineHeight: '26px' }}
            >
              {currentCategory?.category || categoryId?.toUpperCase() || 'Reports'}
            </Typography>
            <Typography
              mt="2px"
              fontWeight={400}
              variant="subtitle2"
              lineHeight="20px"
              color={theme.palette.text.secondary}
            >
              {currentCategory?.description || (categoryId ? `${categoryId.toUpperCase()} dashboards and analytics` : 'Dashboard reports and analytics')}
            </Typography>
          </Box>
        </Box>
        <Divider />

        {/* Main Content */}
        <Box p="20px" pt="10px">
          {loading ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="calc(100vh - 187px)"
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              height="calc(100vh - 187px)"
            >
              <Typography color="error" variant="h6">
                Failed to load reports
              </Typography>
              <Typography color="text.secondary" variant="body2" mt={1}>
                {error}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" gap={3} height="calc(100vh - 187px)">
              {/* Report List Sidebar */}
              <Card
                elevation={0}
                sx={{
                  width: '320px',
                  display: 'flex',
                  minWidth: '320px',
                  borderRadius: '14px',
                  flexDirection: 'column',
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.08)',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                  }}
                >
                  <List sx={{ p: 0 }}>
                    {currentCategory && (Object.keys(currentCategory.reports ?? {}).length > 0 || Object.keys(currentCategory.subcategories ?? {}).length > 0) ? (
                      <>
                        {/* Render flat reports if they exist */}
                        {Object.keys(currentCategory.reports ?? {}).map((reportKey, index) => {
                          const report = currentCategory.reports![reportKey];
                          const isActive = activeReport?.embed_url === report.embed_url && activeReport?.name === report.name;
                          return (
                            <ListItem key={index} sx={{ p: 0 }}>
                              <ListItemButton
                                onClick={() => setActiveReport(report)}
                                sx={{
                                  pl: 2.5,
                                  pr: 2.5,
                                  py: 1.75,
                                  backgroundColor:
                                    isActive
                                      ? theme.palette.primary.main
                                      : 'transparent',
                                  color:
                                    isActive
                                      ? theme.palette.primary.contrastText
                                      : theme.palette.text.primary,
                                  '&:hover': {
                                    backgroundColor:
                                      isActive
                                        ? theme.palette.primary.main
                                        : theme.palette.action.hover,
                                  },
                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="body2"
                                      fontWeight={
                                        isActive
                                          ? 500
                                          : 400
                                      }
                                      color={
                                        isActive
                                          ? theme.palette.primary.contrastText
                                          : theme.palette.text.primary
                                      }
                                    >
                                      {report.name}
                                    </Typography>
                                  }
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}

                        {/* Render subcategories if they exist */}
                        {Object.keys(currentCategory.subcategories ?? {}).map((subcategoryKey) => {
                          const subcategory = currentCategory.subcategories![subcategoryKey];
                          const isExpanded = expandedSubcategories.has(subcategoryKey);
                          
                          return (
                            <React.Fragment key={subcategoryKey}>
                              {/* Subcategory Header */}
                              <ListItem sx={{ p: 0 }}>
                                <ListItemButton
                                  onClick={() => {
                                    const newExpanded = new Set(expandedSubcategories);
                                    if (isExpanded) {
                                      newExpanded.delete(subcategoryKey);
                                    } else {
                                      newExpanded.add(subcategoryKey);
                                    }
                                    setExpandedSubcategories(newExpanded);
                                  }}
                                  sx={{
                                    pl: 2,
                                    pr: 2,
                                    py: 1.5,
                                    backgroundColor: theme.palette.action.hover,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    '&:hover': {
                                      backgroundColor: theme.palette.action.selected,
                                    },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <FolderOpenIcon fontSize="small" color="primary" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color={theme.palette.text.primary}
                                      >
                                        {subcategory.name}
                                      </Typography>
                                    }
                                  />
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </ListItemButton>
                              </ListItem>

                              {/* Subcategory Reports (collapsible) */}
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                  {Object.keys(subcategory.reports).map((reportKey, index) => {
                                    const report = subcategory.reports[reportKey];
                                    const isActive = activeReport?.embed_url === report.embed_url && activeReport?.name === report.name;
                                    return (
                                      <ListItem key={index} sx={{ p: 0 }}>
                                        <ListItemButton
                                          onClick={() => setActiveReport(report)}
                                          sx={{
                                            pl: 5,
                                            pr: 2.5,
                                            py: 1.5,
                                            backgroundColor:
                                              isActive
                                                ? theme.palette.primary.main
                                                : 'transparent',
                                            color:
                                              isActive
                                                ? theme.palette.primary.contrastText
                                                : theme.palette.text.primary,
                                            '&:hover': {
                                              backgroundColor:
                                                isActive
                                                  ? theme.palette.primary.main
                                                  : theme.palette.action.hover,
                                            },
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                          }}
                                        >
                                          <ListItemText
                                            primary={
                                              <Typography
                                                variant="body2"
                                                fontWeight={
                                                  isActive
                                                    ? 500
                                                    : 400
                                                }
                                                color={
                                                  isActive
                                                    ? theme.palette.primary.contrastText
                                                    : theme.palette.text.primary
                                                }
                                              >
                                                {report.name}
                                              </Typography>
                                            }
                                          />
                                        </ListItemButton>
                                      </ListItem>
                                    );
                                  })}
                                </List>
                              </Collapse>
                            </React.Fragment>
                          );
                        })}
                      </>
                    ) : (
                      <Box mt={7} px={2}>
                        <Typography fontSize={14} textAlign="center" color="textDisabled">
                          No reports available in this category yet.
                        </Typography>
                        <Typography fontSize={12} textAlign="center" color="textDisabled" mt={2}>
                          Configure dashboards for this category in the reports service.
                        </Typography>
                      </Box>
                    )}
                  </List>
                </Box>
              </Card>

              {/* Report Viewer */}
              <Box width="100%" height="100%" display="flex" flexDirection="column">
                {/* Parameter Controls */}
                {activeReport?.parameters && activeReport.parameters.length > 0 && (
                  <Card
                    elevation={0}
                    sx={{
                      mb: 2,
                      borderRadius: '14px',
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.background.paper,
                      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.08)',
                    }}
                  >
                    <Box px={2.5} py={2}>
                      <Typography 
                        variant="subtitle2" 
                        mb={2} 
                        fontWeight={500}
                        color={theme.palette.text.primary}
                      >
                        Filters
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        {activeReport.parameters.map((param) => (
                          <TextField
                            key={param.slug}
                            label={param.label}
                            type={param.type.startsWith('date') ? 'date' : 'text'}
                            value={paramValues[param.slug] || ''}
                            onChange={(e) =>
                              setParamValues({
                                ...paramValues,
                                [param.slug]: e.target.value,
                              })
                            }
                            InputLabelProps={{ shrink: true }}
                            inputProps={
                              param.type.startsWith('date')
                                ? {
                                    max: new Date().toISOString().split('T')[0], // No future dates
                                  }
                                : undefined
                            }
                            size="small"
                            sx={{ 
                              minWidth: 220,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Card>
                )}

                {/* Iframe Container */}
                <Box
                  flex={1}
                  width="100%"
                  ref={iframeContainerRef}
                  sx={{
                    overflow: 'hidden',
                    position: 'relative',
                    borderRadius: '14px',
                    '&:fullscreen': { borderRadius: 0 },
                  }}
                >
                  {iFrameUrl ? (
                  <>
                    <iframe
                      width="100%"
                      height="100%"
                      src={iFrameUrl}
                      allowFullScreen
                      allow="fullscreen"
                      id="embedded-report-frame"
                      title="Metabase Report"
                      style={{ border: 'none', display: 'block' }}
                    />

                    {/* Fullscreen toggle button */}
                    <Box right={21} bottom={21} zIndex={1000} position="absolute">
                      <Tooltip
                        arrow
                        title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                        PopperProps={{
                          disablePortal: isFullscreen,
                          container: iframeContainerRef.current ?? undefined,
                        }}
                        slotProps={{
                          tooltip: {
                            sx: {
                              color: 'black',
                              whiteSpace: 'nowrap',
                              backgroundColor: 'white',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            },
                          },
                          arrow: {
                            sx: {
                              color: 'white',
                            },
                          },
                        }}
                      >
                        <IconButton
                          onClick={toggleFullscreen}
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    height="100%"
                  >
                    <Typography textAlign="center" color="textDisabled" variant="h6" mb={2}>
                      No reports available
                    </Typography>
                    <Typography textAlign="center" color="textDisabled" variant="body2">
                      Configure dashboards for this category in the reports service.
                    </Typography>
                  </Box>
                )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CategoryReportsPage;
